import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';

const RecordingStatusPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_schools: 0,
    limit: 50,
  });

  const fetchSchools = async (page = 1, limit = 50) => {
    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'token': localStorage.getItem('token'),
    };

    setLoading(true);
    try {
      const res = await API.get(
        `/internal/recordingStatus/schools?page=${page}&limit=${limit}`,
        { headers }
      );

      if (res.data?.response_code === 1000) {
        const { schools = [], pagination: pg = {} } = res.data.data || {};
        setSchools(schools);
        setPagination(prev => ({
          ...prev,
          current_page: pg.current_page || page,
          total_pages: pg.total_pages || prev.total_pages,
          total_schools: pg.total_schools ?? prev.total_schools,
          limit: pg.limit || limit,
        }));
      } else {
        toast.error(res.data?.message || 'Failed to fetch recording status', {
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch recording status.', { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = e => setSearchQuery(e.target.value);

  // Client-side filter by school name/uuid (applies to currently loaded page)
  const filtered = schools.filter(s => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toUpperCase();
    return (
      (s.school_name || '').toUpperCase().includes(q) ||
      (s.school_uuid || '').toUpperCase().includes(q)
    );
  });

  const num = v => Number(v ?? 0);
  const totalFor = row =>
    num(row.unrecorded) + num(row.recording) + num(row.recorded) + num(row.rerecorded);
  const pct = row => {
    const t = totalFor(row);
    if (!t) return '0.0%';
    return `${((num(row.recorded) / t) * 100).toFixed(1)}%`;
  };

  const chip = (text, bg, fg) => (
    <span
      style={{
        backgroundColor: bg,
        color: fg,
        padding: '5px 10px',
        borderRadius: '12px',
        fontWeight: 'bold',
        display: 'inline-block',
        minWidth: 70,
        textAlign: 'center',
      }}
    >
      {text}
    </span>
  );

  const columns = [
    {
      name: 'School UUID',
      selector: row => row.school_uuid,
      sortable: true,
      wrap: true,
      grow: 1.2,
    },
    {
      name: 'School Name',
      selector: row => row.school_name,
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Unrecorded',
      selector: row => num(row.unrecorded),
      cell: row => chip(num(row.unrecorded), '#f8d7da', '#721c24'),
      sortable: true,
      right: true,
      width: '140px',
    },
    {
      name: 'Recording',
      selector: row => num(row.recording),
      cell: row => chip(num(row.recording), '#cce5ff', '#004085'),
      sortable: true,
      right: true,
      width: '140px',
    },
    {
      name: 'Re-record',
      selector: row => num(row.rerecorded),
      cell: row => chip(num(row.rerecorded), '#fff3cd', '#856404'),
      sortable: true,
      right: true,
      width: '140px',
    },
    {
      name: 'Completed',
      selector: row => num(row.recorded),
      cell: row => chip(num(row.recorded), '#d4edda', '#155724'),
      sortable: true,
      right: true,
      width: '140px',
    },
    {
      name: 'Total',
      selector: row => totalFor(row),
      sortable: true,
      right: true,
      width: '110px',
    },
    {
      name: '% Completed',
      selector: row => pct(row),
      sortable: true,
      right: true,
      width: '140px',
    },
  ];

  const handlePageChange = page => {
    fetchSchools(page, pagination.limit);
  };

  const handlePerPageChange = newLimit => {
    fetchSchools(1, newLimit);
  };

  return (
    <Fragment>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card rounded-25">
              <div className="card-header rounded-25 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Recording Status (Schools)</h5>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by school name / UUID"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ maxWidth: 320 }}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchQuery('');
                    }}
                  >
                    Clear
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchSchools(pagination.current_page, pagination.limit)}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <DataTable
                    columns={columns}
                    data={filtered}
                    striped
                    persistTableHead
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.total_schools}
                    paginationDefaultPage={pagination.current_page}
                    paginationPerPage={pagination.limit}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerPageChange}
                  />
                </div>
                <small className="text-muted">
                  Page {pagination.current_page} of {pagination.total_pages} • Total schools:{' '}
                  {pagination.total_schools}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default RecordingStatusPage;
